import { FC, memo, useEffect, useRef, useCallback, useMemo } from 'react';

import {
  useLazyGetAllUserFollowingShowsQuery,
  useLazyGetAllUserFollowingRecordedShowsQuery,
} from '@redux/queries/user';
import {
  useUserFollowingLiveShows,
  useUserFollowingLiveShowsMayLoadMore,
  useUserFollowingRecordedShows,
  useUserFollowingRecordedShowsMayLoadMore,
} from '@redux/selectors/shows';
import Loader from '@components/loader';
import ShowCard from '@components/show/card';
import { emptyText } from '@utils/constants';
import { useScroll } from '@hooks/useScroll';

import { ShowsContainer, EmptyState } from './styled';

const FollowingFeed: FC = () => {
  const showsContainerRef = useRef<HTMLDivElement>(null);

  const [
    getUserFollowingLiveShows,
    {
      isFetching: isFetchingLiveShows,
      isSuccess: isSuccessLiveShows,
      isUninitialized: isUninitializedLiveShows,
      // isError: isErrorLiveShows,
    },
  ] = useLazyGetAllUserFollowingShowsQuery();
  const [
    getUserFollowingRecordedShows,
    {
      isFetching: isFetchingRecordedShows,
      // isSuccess: isSuccessRecordedShows,
      // isError: isErrorRecordedShows,
    },
  ] = useLazyGetAllUserFollowingRecordedShowsQuery();
  const liveShowsIds = useUserFollowingLiveShows();
  const mayLoadMoreLiveShows = useUserFollowingLiveShowsMayLoadMore();
  const recordedShowsIds = useUserFollowingRecordedShows();
  const mayLoadMoreRecordedShows = useUserFollowingRecordedShowsMayLoadMore();
  // const anyError = useMemo(
  //   () => isErrorLiveShows || isErrorRecordedShows,
  //   [isErrorLiveShows, isErrorRecordedShows],
  // );
  // const anySuccess = useMemo(
  //   () => isSuccessLiveShows || isSuccessRecordedShows,
  //   [isSuccessLiveShows, isSuccessRecordedShows],
  // );

  const followingIds = useMemo(() => {
    if (liveShowsIds.length > 0) {
      if (mayLoadMoreLiveShows) {
        return [...liveShowsIds];
      } else {
        return [...liveShowsIds, ...recordedShowsIds];
      }
    } else {
      return [...recordedShowsIds];
    }
  }, [liveShowsIds, mayLoadMoreLiveShows, recordedShowsIds]);

  const onLoadMore = useCallback(() => {
    if (mayLoadMoreLiveShows) {
      getUserFollowingLiveShows({
        lastId: liveShowsIds[liveShowsIds.length - 1],
        isRefresh: false,
      });
    } else if (mayLoadMoreRecordedShows) {
      getUserFollowingRecordedShows({
        lastId: recordedShowsIds[recordedShowsIds.length - 1],
        isRefresh: false,
      });
    }
  }, [
    mayLoadMoreLiveShows,
    liveShowsIds,
    mayLoadMoreRecordedShows,
    recordedShowsIds,
    getUserFollowingLiveShows,
    getUserFollowingRecordedShows,
  ]);

  useEffect(() => {
    if (isUninitializedLiveShows) {
      getUserFollowingLiveShows({
        isRefresh: true,
      });
    }
  }, [isUninitializedLiveShows, getUserFollowingLiveShows]);

  useEffect(() => {
    if (isSuccessLiveShows && !mayLoadMoreLiveShows) {
      getUserFollowingRecordedShows({
        isRefresh: true,
      });
    }
  }, [isSuccessLiveShows, mayLoadMoreLiveShows, getUserFollowingRecordedShows]);

  useScroll({ onLoadMore, containerRef: showsContainerRef });

  return (
    <ShowsContainer $isEmpty={!followingIds.length} ref={showsContainerRef}>
      {!followingIds.length ? (
        <EmptyState>{emptyText.shows}</EmptyState>
      ) : (
        followingIds.map((id: string) => <ShowCard key={id} showId={id} />)
      )}

      {!!followingIds.length &&
        (isFetchingLiveShows || isFetchingRecordedShows) && (
          <Loader size={'large'} />
        )}
    </ShowsContainer>
  );
};

export default memo(FollowingFeed);
